"use client";

import { useState, useEffect, useMemo } from "react";
import { Loader2, Check } from "lucide-react";
import { useRouter } from "next/navigation";
import { toast } from "sonner";
import Image from "next/image";
import useProductDetail from "@/app/lib/features/productDetail/productDetail.hook";
import useCart from "@/app/lib/features/cart/cart.hook";
import useFavorite from "@/app/lib/features/favorite/favorite.hook";
import useModal from "@/app/lib/features/modal/modal.hook";
import FallbackImage from "../elements/FallbackImage";
import { Button } from "../shadcn/button";
import RotatingText from "./sections/RotationText";
import { Heart } from "lucide-react";
import { trackProductAddToCart, trackProductFavorite } from "@/app/lib/tracking/interactions";

const ProductHeader = () => {
  const router = useRouter();
  const {
    product,
    bottomGroupSections,
    selectedColorIndex,
    selectedSizeIndex,
    updateVariantFavorite,
  } = useProductDetail();
  const { addToCart } = useCart();
  const { toggleFavorite, isToggling } = useFavorite();
  const { createModal } = useModal();
  const [isVisible, setIsVisible] = useState(false);
  const [activeSection, setActiveSection] = useState("");
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [cartAdded, setCartAdded] = useState(false);

  const navigationItems = useMemo(() => {
    const items = [];
    
    if (bottomGroupSections && bottomGroupSections.length > 0) {
      bottomGroupSections.forEach((section) => {
        const sectionId = section.key.replace(/_/g, "-");
        items.push({
          id: sectionId,
          label: section.title.toLocaleUpperCase("tr"),
        });
      });
    }
    
    items.push(
      { id: "product-reviews", label: "ÜRÜN DEĞERLENDİRMELERİ" },
      { id: "product-information", label: "ÜRÜN BİLGİLERİ" }
    );
    
    return items;
  }, [bottomGroupSections]);

  const socialProofItems = useMemo(() => {
    if (!product?.social_proof?.items) return [];
    return product.social_proof.items.map((item) => {
      // Use icon_full from API if available
      const iconElement = item.icon_full ? (
        <Image
          src={item.icon_full}
          alt={item.icon}
          width={16}
          height={16}
          className="w-2.5 h-2.5 sm:w-4 sm:h-4 shrink-0"
        />
      ) : null;
      
      // Use style prop for color like in productCard.utils.tsx
      const colorStyle = item.color_hex ? { color: item.color_hex } : undefined;
      
      return {
        text: item.text,
        icon: iconElement,
        style: colorStyle, // Pass color via style prop
      };
    });
  }, [product]);

  useEffect(() => {
    // rAF throttle: her scroll event'inde değil, frame başına en fazla bir kez
    // ölç — getBoundingClientRect sorguları zorunlu yeniden düzenleme (forced
    // reflow) üretiyordu (PageSpeed bulgusu).
    let rafId: number | null = null;

    const measure = () => {
      rafId = null;
      const buttonsElement = document.querySelector("[data-product-buttons]");
      if (buttonsElement) {
        const rect = buttonsElement.getBoundingClientRect();
        setIsVisible(rect.bottom < 0);
      }

      const headerHeight = 140;
      const viewportCenter = window.innerHeight / 2;
      let closestSection = navigationItems[0]?.id || "";
      let closestDistance = Infinity;

      navigationItems.forEach((item) => {
        const element = document.getElementById(item.id);
        if (element) {
          const rect = element.getBoundingClientRect();
          const elementCenter = rect.top + rect.height / 2;
          const distance = Math.abs(elementCenter - viewportCenter);

          if (
            rect.bottom > headerHeight &&
            rect.top < window.innerHeight &&
            distance < closestDistance
          ) {
            closestDistance = distance;
            closestSection = item.id;
          }
        }
      });

      setActiveSection(closestSection);
    };

    const handleScroll = () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(measure);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, [navigationItems]);

  const handleSectionClick = (sectionId: string) => {
    setActiveSection(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      const headerHeight = 140;
      const elementPosition =
        element.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({
        top: elementPosition - headerHeight,
        behavior: "smooth",
      });
    }
  };

  if (!product) return null;

  const productId = product.id;

  const selectedColor = product.variants_by_color?.[selectedColorIndex];
  // Favori durumu doğrudan API verisinden: seçili rengin is_favorited'ı.
  const favoriteVariantId = selectedColor?.variant_id ?? product.variant_id;
  const isFav = selectedColor?.is_favorited ?? product.is_favorited ?? false;
  const isTogglingFav = isToggling(productId);

  // Stok kuralı ProductActions ile BİREBİR aynı olmak zorunda: bu sticky başlık
  // aynı ürünün aynı butonlarını gösteriyor, sayfayı kaydırınca kural değişemez.
  //   isOutOfStock  → seçili beden tükenmiş
  //   !hasAnyStock  → seçili renkte hiçbir beden kalmamış (beden seçilmemişse de
  //                   buton kapanmalı, yoksa size-picker açılıp boş liste çıkar)
  // Kaynak: app/components/common/product-detail/productInformation/more/ProductActions.tsx
  const selectedSize =
    selectedSizeIndex !== -1
      ? selectedColor?.sizes?.[selectedSizeIndex]
      : undefined;
  const isOutOfStock = selectedSize?.stock === 0;
  const hasAnyStock = selectedColor?.sizes?.some((s) => s.stock > 0) ?? false;
  const isUnavailable = isOutOfStock || !hasAnyStock;

  const execAddToCart = async (colorIdx: number, sizeIdx: number) => {
    const color = product.variants_by_color?.[colorIdx];
    const size = color?.sizes?.[sizeIdx];
    if (!size) return;

    setIsAddingToCart(true);

    const result = await addToCart(
      { product_id: productId, variant_id: size.id, quantity: 1 },
      false,
      { name: product.name, price: product.price, brand: product.brand_name ?? undefined, affiliation: product.seller?.name, category: product.category?.name },
    );

    if (result.success) {
      trackProductAddToCart(productId);
      createModal("added-to-cart", {
        image: color?.primary_image ?? product.images?.[0] ?? "",
        name: product.internet_product_description || product.name,
        colorName: color?.color_name,
        sizeName: size.size,
        price: `${(product.price as number).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺`,
      });
      setCartAdded(true);
      setTimeout(() => { setCartAdded(false); setIsAddingToCart(false); }, 2000);
    } else {
      toast.error(result.message ?? "Sepete eklenirken bir hata oluştu");
      setIsAddingToCart(false);
    }
  };

  const execBuyNow = async (colorIdx: number, sizeIdx: number) => {
    const color = product.variants_by_color?.[colorIdx];
    const size = color?.sizes?.[sizeIdx];
    if (!size) return;

    const result = await addToCart(
      { product_id: productId, variant_id: size.id, quantity: 1 },
      false,
      { name: product.name, price: product.price, brand: product.brand_name ?? undefined, affiliation: product.seller?.name, category: product.category?.name },
    );

    if (result.success) {
      trackProductAddToCart(productId);
      router.push("/sepet");
    } else {
      toast.error(result.message ?? "Sepete eklenirken bir hata oluştu");
    }
  };

  const openSizePicker = (action: "addToCart" | "buyNow") => {
    createModal("size-picker", {
      initialColorIndex: selectedColorIndex,
      initialSizeIndex: selectedSizeIndex,
      onConfirm: (colorIdx: number, sizeIdx: number) => {
        if (action === "addToCart") execAddToCart(colorIdx, sizeIdx);
        else execBuyNow(colorIdx, sizeIdx);
      },
    });
  };

  const handleBuyNow = () => {
    if (selectedSizeIndex === -1) { openSizePicker("buyNow"); return; }
    execBuyNow(selectedColorIndex, selectedSizeIndex);
  };

  const handleAddToCart = () => {
    if (selectedSizeIndex === -1) { openSizePicker("addToCart"); return; }
    execAddToCart(selectedColorIndex, selectedSizeIndex);
  };

  const handleToggleFavorite = async () => {
    const success = await toggleFavorite(productId, favoriteVariantId);

    if (success) {
      updateVariantFavorite(favoriteVariantId, !isFav);
      if (!isFav) trackProductFavorite(productId);
      toast.success(isFav ? "Favorilerden çıkarıldı" : "Favorilere eklendi");
    } else {
      toast.error("Bir hata oluştu");
    }
  };

  return (
    <div data-ph-wrapper className="fixed top-0 left-0 right-0 z-50 pointer-events-none">
        <div
          data-ph-inner
          className="bg-white border-b shadow-sm pointer-events-auto"
          style={{
            transform: isVisible ? "translateY(0)" : "translateY(-100%)",
            transition: "transform 0.22s cubic-bezier(0.25, 0.1, 0.25, 1)",
          }}
        >
          <div className="container-minify">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between py-2 sm:py-3 border-b gap-3 sm:gap-0">
              <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto">
                <div className="relative w-10 sm:w-12 h-10 sm:h-12 rounded-md overflow-hidden border shrink-0">
                  <FallbackImage
                    src={product.images[0]}
                    alt={product.internet_product_description}
                    fill
                    className="object-cover"
                    sizes="(max-width: 640px) 40px, 48px"
                  />
                </div>
                <div className="flex flex-col min-w-0 flex-1">
                  <h3 className="text-xs sm:text-sm font-medium line-clamp-1 sm:w-80">
                    <span className="font-medium">{product.brand_name}</span>{" "}
                    {product.internet_product_description}
                  </h3>
                  <div className="flex items-center gap-2">
                    <span className="text-base sm:text-lg font-bold text-main">
                      {product.price.toLocaleString("tr-TR", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}{" "}
                      {product.currency_symbol}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1 sm:gap-2 w-full sm:w-auto justify-between sm:justify-end">
                {socialProofItems.length > 0 && (
                  <div className="hidden md:block">
                    <RotatingText
                      items={socialProofItems}
                      mainClassName="text-xs sm:text-sm"
                      rotationInterval={3000}
                      className="justify-start py-0 text-black"
                    />
                  </div>
                )}
                <div className="hidden sm:flex items-center gap-1 sm:gap-2">
                  <Button
                    type="button"
                    variant="outline-main"
                    size="default"
                    onClick={handleBuyNow}
                    disabled={isUnavailable}
                  >
                    Şimdi Al
                  </Button>
                  <Button
                    type="button"
                    size="default"
                    onClick={handleAddToCart}
                    disabled={isAddingToCart || isUnavailable}
                  >
                    {isAddingToCart ? (
                      cartAdded ? (
                        <>
                          <Check className="mr-1 h-3 w-3" />
                          <span className="text-xs">Eklendi</span>
                        </>
                      ) : (
                        <>
                          <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                          <span className="text-xs">Ekleniyor</span>
                        </>
                      )
                    ) : (
                      "Sepete Ekle"
                    )}
                  </Button>
                  <Button
                    type="button"
                    size="icon-lg"
                    variant={isFav ? "default" : "outline"}
                    onClick={handleToggleFavorite}
                    disabled={isTogglingFav}
                    className={isFav ? "bg-red-500 hover:bg-red-600 border-red-500" : ""}
                  >
                    {isTogglingFav ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <Heart size={14} className={isFav ? "fill-current" : ""} />
                    )}
                  </Button>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3 sm:gap-6 py-2 overflow-x-auto scrollbar-hide">
              {navigationItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => handleSectionClick(item.id)}
                  className={`cursor-pointer text-xs sm:text-sm font-medium whitespace-nowrap transition-colors shrink-0 ${
                    activeSection === item.id
                      ? "text-black"
                      : "text-muted-foreground hover:text-black"
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        </div>
    </div>
  );
};

export default ProductHeader;